import { FormControlProps, Grid, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { includes, isEmpty, map } from 'es-toolkit/compat';
import React, { RefObject } from 'react';

export interface DropdownProps<Multiple extends boolean = false> extends FormControlProps {
  value: any;
  options: DropdownOption[];
  onOptionSelected: Multiple extends false ? (value: string) => void : (value: string[]) => void;
  placeholder?: string;
  label?: React.ReactNode;
  labelId?: string;
  inputRef?: RefObject<HTMLSelectElement>;
  dataTestId?: string;
  disabled?: boolean;
  multiple?: Multiple;
  addSeparatorToOptions?: boolean;
  separatorBeforeOptionIndex?: number;
}

const Dropdown = <Multiple extends boolean = false>({
  options,
  value,
  onOptionSelected,
  placeholder,
  label = '',
  labelId,
  inputRef,
  dataTestId,
  size,
  variant,
  addSeparatorToOptions,
  separatorBeforeOptionIndex,
  disabled = false,
  multiple = false as Multiple,
}: DropdownProps<Multiple>) => {
  const defaultValue = includes(map(options, 'value'), value) ? value : multiple ? [] : '';

  const handleOptionSelected = (event: SelectChangeEvent<Multiple extends false ? string : string[]>) => {
    onOptionSelected(event.target.value as string & string[]);
  };

  return (
    <Select
      labelId={labelId}
      multiple={multiple}
      disabled={disabled}
      size={size}
      variant={variant}
      inputRef={inputRef}
      label={label}
      defaultValue={defaultValue}
      onChange={handleOptionSelected}
      value={value}
      placeholder={placeholder}
      fullWidth
      data-testid={dataTestId}
      data-cy={dataTestId}
    >
      {!isEmpty(options) &&
        map(options, (option, idx) => {
          return (
            <MenuItem
              key={label + option.value}
              value={option.value}
              sx={
                (idx > 0 && addSeparatorToOptions) || idx === separatorBeforeOptionIndex
                  ? { borderTop: 1, borderColor: 'divider' }
                  : undefined
              }
            >
              {option.details ? (
                <Grid container flexDirection="column">
                  <Typography variant="body1" component="span" mr={2}>
                    {option.text}
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                    {option.details}
                  </Typography>
                </Grid>
              ) : (
                option.text
              )}
            </MenuItem>
          );
        })}
    </Select>
  );
};

export interface DropdownOption {
  type?: string;
  value: any;
  text: string | null;
  details?: string;
}

export default Dropdown;
